import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { publicPricing, updatePricing } from "../../api/billing";
import { tokenPricing } from "../../api/wallet";
import { NeuButton, NeuCard, NeuInput } from "../../components/neu/Neu";
import { usePerms } from "../../hooks/usePerms";
import { UnitLabel } from "./UnitLabel";
import { unitOf } from "./pricingUnits";
import { TierRow } from "./TierRow";

const TOKENS_PER_EMP = 52; // 2 × 26

const SETTINGS = [
  ["currency", "platformExtra.currency", "pricing-currency", "text", "platformExtra.unitIso"],
  ["annual_discount_percent", "platformExtra.annualDiscount", "pricing-discount", "number", "percent"],
  ["trial_days", "platformExtra.trialDays", "pricing-trial", "number", "platformExtra.unitDay"],
  ["included_branches", "platformExtra.includedBranches", "pricing-included-branches", "number", "platformExtra.unitBranch"],
  ["price_per_branch", "platformExtra.pricePerBranch", "pricing-branch-price", "number", "per_branch"],
  ["tax_percent", "platformExtra.taxPercent", "pricing-tax", "number", "percent"],
];

export const PricingSettings = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [tokenPrice, setTokenPrice] = useState(0);
  const { can } = usePerms();

  useEffect(() => {
    if (!can("billing")) return;
    (async () => {
      const [doc, tok] = await Promise.all([publicPricing(), tokenPricing()]);
      delete doc._id;
      setConfig(doc);
      setTokenPrice(Number(tok?.price_per_token) || 0);
    })();
  }, [can]);

  if (!config || !can("billing")) return null;
  const currency = config.currency || "USD";

  const save = async () => {
    try { await updatePricing(config); toast.success(t("platformExtra.pricingSaved")); }
    catch { toast.error(t("errors.generic")); }
  };

  return (
    <NeuCard data-testid="pricing-settings">
      <p className="label-xs">{t("platformExtra.pricing")}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {SETTINGS.map(([key, label, testId, type, unit]) => (
          <div key={key}>
            <UnitLabel text={t(label)} unit={unitOf(unit, currency, t)} />
            <NeuInput data-testid={testId} type={type} value={config[key]}
                      onChange={(e) =>
                        setConfig({ ...config, [key]: type === "number" ? Number(e.target.value) : e.target.value })
                      } />
          </div>
        ))}
      </div>

      <div className="mt-5" data-testid="tiers-readonly">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-black">{t("platformExtra.tiersReadonlyTitle")}</p>
        </div>
        <p className="mt-1 mb-3 text-[11px] text-muted-foreground">{t("platformExtra.tiersReadonlyHint")}</p>
        <ul className="space-y-3">
          {config.tiers.map((tier, i) => (
            <TierRow key={i} index={i} readonly currency={currency}
                     tier={{ ...tier, price_per_employee: +(tokenPrice * TOKENS_PER_EMP).toFixed(4) }} />
          ))}
        </ul>
      </div>

      <NeuButton variant="primary" data-testid="pricing-save" onClick={save} className="mt-4">
        {t("platformExtra.savePricing")}
      </NeuButton>
    </NeuCard>
  );
};
